import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Header from '../../components/Header/Header';
import Button from '../../components/Button/Button';
import StockReportFilters from './StockReportFilters';
import useStockReportJob from './useStockReportJob';
import icon from '../../brand/generated/sidebar/report.svg';
import getStockReportFilters from '../../api/cars/getStockReportFilters';
import styles from './stockReport.module.css';

const toOption = (item) => ({ value: item.id, label: item.name });
const toIds = (selected) => (selected || []).map((item) => item.value);

// Общая логика каскада "список зависит от родителя" (модель <- марка, ДЦ <- город):
// optionsFor — опции, суженные под выбранных родителей (пусто = без ограничения);
// keepMatching — из уже выбранных значений оставить только те, что все еще соответствуют
// переданным id родителей (используется сразу после смены родителя, до ре-рендера).
const useChildOf = (items, parentKey) => {
  const parentIdOf = useMemo(
    () => new Map(items.map((item) => [item.id, item[parentKey]])),
    [items, parentKey]
  );

  const optionsFor = useCallback(
    (selectedParentIds) => {
      const filtered =
        selectedParentIds.length === 0
          ? items
          : items.filter((item) => selectedParentIds.includes(item[parentKey]));
      return filtered.map(toOption);
    },
    [items, parentKey]
  );

  const keepMatching = useCallback(
    (selection, selectedParentIds) =>
      selection.filter((item) => selectedParentIds.includes(parentIdOf.get(item.value))),
    [parentIdOf]
  );

  return { optionsFor, keepMatching };
};

const StockReport = () => {
  const [marks, setMarks] = useState([]);
  const [models, setModels] = useState([]);
  const [cities, setCities] = useState([]);
  const [dcs, setDcs] = useState([]);

  const [selectedMarks, setSelectedMarks] = useState([]);
  const [selectedModels, setSelectedModels] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedDcs, setSelectedDcs] = useState([]);

  const { phase, progress, errorMessage, doneTotal, start, downloadAgain } = useStockReportJob();

  useEffect(() => {
    getStockReportFilters()
      .then((res) => {
        setMarks(res.data.marks || []);
        setModels(res.data.models || []);
        setCities(res.data.cities || []);
        setDcs(res.data.dcs || []);
      })
      .catch(() => {
        // Фильтры не критичны — отчет можно сформировать и без них
      });
  }, []);

  const modelsOfMark = useChildOf(models, 'markId');
  const dcsOfCity = useChildOf(dcs, 'cityId');

  const handleMarksChange = (vals) => {
    const selected = vals || [];
    setSelectedMarks(selected);
    if (selected.length === 0) return;
    const markIds = selected.map((m) => m.value);
    setSelectedModels((prev) => modelsOfMark.keepMatching(prev, markIds));
  };

  const handleCitiesChange = (vals) => {
    const selected = vals || [];
    setSelectedCities(selected);
    if (selected.length === 0) return;
    const cityIds = selected.map((c) => c.value);
    setSelectedDcs((prev) => dcsOfCity.keepMatching(prev, cityIds));
  };

  const markOptions = useMemo(() => marks.map(toOption), [marks]);
  const modelOptions = useMemo(
    () => modelsOfMark.optionsFor(selectedMarks.map((m) => m.value)),
    [modelsOfMark, selectedMarks]
  );
  const cityOptions = useMemo(() => cities.map(toOption), [cities]);
  const dcOptions = useMemo(
    () => dcsOfCity.optionsFor(selectedCities.map((c) => c.value)),
    [dcsOfCity, selectedCities]
  );

  const handleStart = (simulateError = false) => {
    start(
      {
        markIds: toIds(selectedMarks),
        modelIds: toIds(selectedModels),
        cityIds: toIds(selectedCities),
        dcIds: toIds(selectedDcs),
      },
      simulateError
    );
  };

  const running = phase === 'running';
  const downloading = phase === 'downloading';
  const busy = running || downloading;
  const preparing = running && progress.total === 0;
  const finishing = running && !preparing && progress.percent >= 100;
  const indeterminate = preparing || finishing || downloading;

  return (
    <>
      <Header title="Отчет по стоку" icon={icon} />
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.cardSubtitle}>
            Фильтры ниже не обязательны: если ничего не выбрано, выгрузится весь сток.
          </div>

          <StockReportFilters
            markOptions={markOptions}
            modelOptions={modelOptions}
            cityOptions={cityOptions}
            dcOptions={dcOptions}
            selectedMarks={selectedMarks}
            selectedModels={selectedModels}
            selectedCities={selectedCities}
            selectedDcs={selectedDcs}
            onMarksChange={handleMarksChange}
            onModelsChange={(vals) => setSelectedModels(vals || [])}
            onCitiesChange={handleCitiesChange}
            onDcsChange={(vals) => setSelectedDcs(vals || [])}
            disabled={busy}
          />

          <div className={styles.actions}>
            <Button
              fill
              text={busy ? 'Формируется...' : 'Сформировать отчет'}
              onClick={() => handleStart(false)}
              disabled={busy}
            />
          </div>

          {busy && (
            <div className={styles.progressBlock}>
              <div className={styles.progressLabelRow}>
                <span className={styles.progressLabel}>
                  {downloading
                    ? 'Файл готов, загрузка...'
                    : preparing
                    ? 'Подготовка данных...'
                    : finishing
                    ? 'Подготовка файла отчета...'
                    : `Обработано ${progress.processed} из ${progress.total} автомобилей`}
                </span>
                {running && !preparing && !finishing && (
                  <span className={styles.progressPercent}>{progress.percent}%</span>
                )}
              </div>
              <div className={styles.progressTrack}>
                <div
                  className={`${styles.progressFill} ${indeterminate ? styles.progressIndeterminate : ''}`}
                  style={indeterminate ? undefined : { width: `${progress.percent}%` }}
                />
              </div>
            </div>
          )}

          {phase === 'success' && (
            <div className={styles.resultSuccess}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M10 20C4.477 20 0 15.523 0 10C0 4.477 4.477 0 10 0C15.523 0 20 4.477 20 10C20 15.523 15.523 20 10 20ZM10 18C12.1217 18 14.1566 17.1571 15.6569 15.6569C17.1571 14.1566 18 12.1217 18 10C18 7.87827 17.1571 5.84344 15.6569 4.34315C14.1566 2.84285 12.1217 2 10 2C7.87827 2 5.84344 2.84285 4.34315 4.34315C2.84285 5.84344 2 7.87827 2 10C2 12.1217 2.84285 14.1566 4.34315 15.6569C5.84344 17.1571 7.87827 18 10 18ZM9.003 14L4.76 9.757L6.174 8.343L9.003 11.172L14.659 5.515L16.074 6.929L9.003 14Z"
                  fill="#5cb85c"
                />
              </svg>
              <div className={styles.resultText}>
                <span>Отчет сформирован и скачан — {doneTotal} авто.</span>
                <button type="button" className={styles.linkButton} onClick={downloadAgain}>
                  Скачать еще раз
                </button>
              </div>
            </div>
          )}

          {phase === 'empty' && (
            <div className={styles.resultEmpty}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M10 20C4.477 20 0 15.523 0 10C0 4.477 4.477 0 10 0C15.523 0 20 4.477 20 10C20 15.523 15.523 20 10 20ZM10 18C12.1217 18 14.1566 17.1571 15.6569 15.6569C17.1571 14.1566 18 12.1217 18 10C18 7.87827 17.1571 5.84344 15.6569 4.34315C14.1566 2.84285 12.1217 2 10 2C7.87827 2 5.84344 2.84285 4.34315 4.34315C2.84285 5.84344 2 7.87827 2 10C2 12.1217 2.84285 14.1566 4.34315 15.6569C5.84344 17.1571 7.87827 18 10 18ZM9 9H11V15H9V9ZM9 5H11V7H9V5Z"
                  fill="#857f74"
                />
              </svg>
              <div className={styles.resultText}>
                <span>По выбранным фильтрам в стоке нет ни одного автомобиля.</span>
                <button type="button" className={styles.linkButton} onClick={() => handleStart(false)}>
                  Попробовать снова
                </button>
              </div>
            </div>
          )}

          {phase === 'error' && (
            <div className={styles.resultError}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M10 20C4.477 20 0 15.523 0 10C0 4.477 4.477 0 10 0C15.523 0 20 4.477 20 10C20 15.523 15.523 20 10 20ZM10 18C12.1217 18 14.1566 17.1571 15.6569 15.6569C17.1571 14.1566 18 12.1217 18 10C18 7.87827 17.1571 5.84344 15.6569 4.34315C14.1566 2.84285 12.1217 2 10 2C7.87827 2 5.84344 2.84285 4.34315 4.34315C2.84285 5.84344 2 7.87827 2 10C2 12.1217 2.84285 14.1566 4.34315 15.6569C5.84344 17.1571 7.87827 18 10 18ZM9 13H11V15H9V13ZM9 5H11V11H9V5Z"
                  fill="#d9534f"
                />
              </svg>
              <div className={styles.resultText}>
                <span>{errorMessage}</span>
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={() => handleStart(false)}
                >
                  Попробовать снова
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default StockReport;
